import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minus,
  Plus,
  RotateCw,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { cn } from "@/lib/utils";

// ===========================================================================
// EL VISOR DE PAPELES.
//
// Contabilidad pidió «poder ver el PDF completo adjunto y poder agrandar o
// maximizar el voucher o ficha». Lo que había eran dos `<img>` de altura fija
// —`max-h-[460px]`— sin `onClick`, sin diálogo y sin enlace: un voucher de
// móvil a 460 px no se lee, el número de operación son seis dígitos de 8 px, y
// quien revisa acababa decidiendo por el importe.
//
// Y del expediente se enseñaba UNA hoja de las cinco que trae de media: la
// página donde se encontró el comprobante. La ficha del cliente y el voucher
// están en las otras.
//
// DOS COSAS QUE PARECEN DETALLE Y NO LO SON:
//
// 1. Las páginas se piden DIBUJADAS, una por una, a `/pagina/:archivo/:n`. No
//    se sirve el PDF original a un `<iframe>`, que sería lo cómodo: ese
//    endpoint dibuja con `renderizarPagina(..., await rucsDelGrupo())` y ese
//    último argumento TACHA LOS RUC DEL GRUPO. Servir el PDF salta la
//    tachadura. Cuesta una petición por hoja y se paga.
//
// 2. El zoom es `transform: scale`, no `width`. Con `width` el navegador
//    reescala la imagen ya decodificada y el texto se empasta justo cuando se
//    amplía para leerlo; con `scale` sobre una imagen de 1.400 px de ancho, los
//    dígitos aguantan.
// ===========================================================================

export type Papel =
  /** Una imagen suelta: el recorte de un voucher. */
  | { clase: "imagen"; recorte: string; titulo: string }
  /** Un expediente entero, que se recorre hoja por hoja. */
  | {
      clase: "expediente";
      archivoId: string;
      /** Por cuál abrir. */
      pagina: number;
      /** Cuántas tiene. `null` si no se sabe: entonces no se pasa página. */
      paginas: number | null;
      titulo: string;
    };

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 6;
const PASO = 0.25;

export function Visor({
  papel,
  alCerrar,
}: {
  papel: Papel | null;
  alCerrar: () => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [giro, setGiro] = useState(0);
  const [pagina, setPagina] = useState(1);

  // Al cambiar de papel se vuelve a empezar: heredar el zoom del anterior deja
  // la hoja nueva fuera de la pantalla y parece que no cargó.
  useEffect(() => {
    setZoom(1);
    setGiro(0);
    setPagina(papel?.clase === "expediente" ? papel.pagina : 1);
  }, [papel]);

  const total = papel?.clase === "expediente" ? (papel.paginas ?? 1) : 1;
  const mover = useCallback(
    (d: -1 | 1) => setPagina((p) => Math.min(total, Math.max(1, p + d))),
    [total],
  );

  // El teclado, que es como se recorre un expediente de cinco hojas sin soltar
  // la vista del papel.
  useEffect(() => {
    if (!papel) return;
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === "Escape") alCerrar();
      else if (e.key === "ArrowRight") mover(1);
      else if (e.key === "ArrowLeft") mover(-1);
      else if (e.key === "+" || e.key === "=")
        setZoom((z) => Math.min(ZOOM_MAX, z + PASO));
      else if (e.key === "-") setZoom((z) => Math.max(ZOOM_MIN, z - PASO));
      else if (e.key.toLowerCase() === "r") setGiro((g) => (g + 90) % 360);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [papel, alCerrar, mover]);

  if (!papel) return null;

  const src =
    papel.clase === "imagen"
      ? `/imagen/${papel.recorte}`
      : `/pagina/${papel.archivoId}/${pagina}`;
  const esExpediente = papel.clase === "expediente";

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: el fondo cierra al
    // pulsarlo, que es lo que espera cualquiera de un diálogo a pantalla
    // completa; el teclado tiene su Escape más arriba.
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-sm"
      onClick={alCerrar}
      role="dialog"
      aria-modal="true"
      aria-label={papel.titulo}
    >
      {/* La barra. `stopPropagation` para que pulsar un botón no cierre. */}
      <div
        className="flex flex-wrap items-center gap-2 border-white/10 border-b px-4 py-2.5 text-white"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <span className="truncate font-medium text-[13px]">{papel.titulo}</span>

        {esExpediente && total > 1 ? (
          <span className="ml-2 flex items-center gap-1.5">
            <BotonBarra
              alPulsar={() => mover(-1)}
              inhabilitado={pagina <= 1}
              titulo="Hoja anterior (←)"
            >
              <ChevronLeft className="size-4" />
            </BotonBarra>
            <span className="min-w-[5.5rem] text-center text-[12px] tabular-nums">
              hoja {pagina} de {total}
            </span>
            <BotonBarra
              alPulsar={() => mover(1)}
              inhabilitado={pagina >= total}
              titulo="Hoja siguiente (→)"
            >
              <ChevronRight className="size-4" />
            </BotonBarra>
          </span>
        ) : null}

        <span className="ml-auto flex items-center gap-1.5">
          <BotonBarra
            alPulsar={() => setZoom((z) => Math.max(ZOOM_MIN, z - PASO))}
            inhabilitado={zoom <= ZOOM_MIN}
            titulo="Alejar (−)"
          >
            <Minus className="size-4" />
          </BotonBarra>
          <span className="min-w-[3.5rem] text-center text-[12px] tabular-nums">
            {Math.round(zoom * 100)} %
          </span>
          <BotonBarra
            alPulsar={() => setZoom((z) => Math.min(ZOOM_MAX, z + PASO))}
            inhabilitado={zoom >= ZOOM_MAX}
            titulo="Acercar (+)"
          >
            <Plus className="size-4" />
          </BotonBarra>
          <BotonBarra alPulsar={() => setZoom(1)} titulo="Tamaño original">
            <Maximize2 className="size-4" />
          </BotonBarra>
          <BotonBarra
            alPulsar={() => setGiro((g) => (g + 90) % 360)}
            titulo="Girar (R)"
          >
            <RotateCw className="size-4" />
          </BotonBarra>
          <BotonBarra alPulsar={alCerrar} titulo="Cerrar (Esc)">
            <X className="size-4" />
          </BotonBarra>
        </span>
      </div>

      {/* El papel. El contenedor scrollea cuando el zoom lo saca de pantalla. */}
      <div className="flex-1 overflow-auto p-4">
        <div className="flex min-h-full items-center justify-center">
          {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: la
              imagen no cierra al pulsarla — se amplía y se arrastra. */}
          <img
            key={src}
            src={src}
            alt={papel.titulo}
            onClick={(e) => e.stopPropagation()}
            className="max-w-none rounded bg-white shadow-2xl transition-transform"
            style={{
              // `scale` y no `width`: reescalar la imagen decodificada empasta
              // el texto justo cuando se amplía para poder leerlo.
              transform: `scale(${zoom}) rotate(${giro}deg)`,
              transformOrigin: "center",
              maxHeight: zoom === 1 ? "calc(100vh - 8rem)" : undefined,
            }}
          />
        </div>
      </div>

      <p className="px-4 pb-2 text-[11px] text-white/50">
        Esc cierra · + y − acercan · R gira
        {esExpediente && total > 1 ? " · ← y → pasan de hoja" : ""}
      </p>
    </div>
  );
}

function BotonBarra({
  children,
  alPulsar,
  inhabilitado,
  titulo,
}: {
  children: React.ReactNode;
  alPulsar: () => void;
  inhabilitado?: boolean;
  titulo: string;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      disabled={inhabilitado}
      title={titulo}
      aria-label={titulo}
      className={cn(
        "grid size-7 place-items-center rounded border border-white/20 transition-colors",
        inhabilitado ? "opacity-30" : "hover:bg-white/15",
      )}
    >
      {children}
    </button>
  );
}
